<!-- eslint-disable vue/no-v-text-v-html-on-component -->
<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="activity-card-container">
    <div v-for="notification in visibleNotifications" :key="notification.id" class="card">
      <div class="card-header">
        <Avatar
          :name="getParticipantName(notification.participantId)"
          :deleted-at="getParticipantDeletedAt(notification.participantId)"
        />
        <div>
          <FriedParagraph :class="[{ removed: isParticipantRemoved(notification.participantId) }]">
            <span class="participant">
              {{
                getParticipantName(notification.participantId) || t('timeline_removed_participant')
              }}
            </span>
            {{ notification.eventName }}
            <span v-if="notification.eventTitle"> - {{ notification.eventTitle }} </span>
          </FriedParagraph>
          <FriedParagraph :size="ParagraphSize.Small" class="time">{{
            getTime(notification.createdAt)
          }}</FriedParagraph>
        </div>
        <FriedIconButton
          v-if="!notification.isRead"
          v-tooltip="{
            content: t('hover_tooltip_bluedot'),
            delay: { show: 300, hide: 100 },
            placement: 'left',
            distance: 16,
          }"
          class="mark-read-button"
          :data-external="`activity-mark-as-read-button-${notification.createdAt}`"
          @click="$emit('set-viewed', notification.id)"
        >
          <div class="blue-dot"></div>
        </FriedIconButton>
      </div>
      <template v-if="hasMetaDataContent(notification)">
        <FriedSkeleton v-if="!notification.metaData.content" type="paragraph" :rows="1" />
        <FriedParagraph
          v-else-if="notification.metaData.isContentAvailable"
          class="content"
          v-html="notification.metaData.content"
        />
        <div v-else class="unavailable-content">
          <FriedIcon icon="alert-circle" size="small" />
          <FriedParagraph class="content">
            {{ notification.metaData.content }}
          </FriedParagraph>
        </div>
      </template>
      <FriedButton
        v-if="canNavigateTo(notification)"
        v-tooltip="{
          content: disabledCtaTooltip(notification),
          delay: { show: 300, hide: 100 },
          distance: 16,
        }"
        class="go-there-button"
        :data-external="`activity-go-there-cta-button-${notification.id}`"
        :disabled="featureIsDisabled(notification.type) || !isContentAvailable(notification)"
        :button-type="ButtonType.Tertiary"
        @click="$emit('navigate-to', notification)"
      >
        {{ t('dr_event_card_cta') }}
      </FriedButton>
    </div>
    <button
      v-if="!!nonVisibleNotifications"
      class="show-more-button"
      data-external="activity-card-show-more-button"
      @click="showMore = true"
    >
      <FriedH4>
        {{ t('dr_activity_show_more', { number: nonVisibleNotifications }) }}
      </FriedH4>
      <FriedIcon icon="chevron-down" />
    </button>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import { defineComponent, computed, ref } from 'vue';
import { ParagraphSize, ButtonType } from '@getaccept/fried-tofu';
import { DateTime } from 'luxon';
import { useI18n } from 'vue-i18n';
import AvatarHelper from '../../helpers/avatar.helper';
import Avatar from '../../components/Avatar.vue';
import type { DealroomSettings } from '../../types/dsr';
import type { Participant } from '../../types/participant';
import { DealroomEventType } from '../../api/timeline/types/log.types';
import NotificationConstants from '../constants/notification.constants';
import type { NotificationEvent } from '../types';

export default defineComponent({
  components: {
    Avatar,
  },
  props: {
    notifications: {
      type: Array as PropType<NotificationEvent[]>,
      required: true,
    },
    roomSettings: {
      type: Object as PropType<DealroomSettings>,
      default: null,
    },
    participants: {
      type: Array as PropType<Participant[]>,
      required: true,
    },
  },
  emits: ['navigate-to', 'set-viewed'],
  setup(props) {
    const { t } = useI18n();
    const showMore = ref(false);

    const visibleNotifications = computed(() =>
      showMore.value ? props.notifications : props.notifications.slice(0, 3)
    );

    const nonVisibleNotifications = computed(
      () => props.notifications.length - visibleNotifications.value.length
    );

    const isContentAvailable = (notification: NotificationEvent) => {
      if (!notification.metaData) {
        return true;
      }
      return !!notification.metaData.isContentAvailable;
    };

    const featureIsDisabled = (type: DealroomEventType) => {
      switch (type) {
        case DealroomEventType.TaskCreated:
        case DealroomEventType.TaskDeleted:
        case DealroomEventType.TaskUpdated:
        case DealroomEventType.MilestoneCreated:
        case DealroomEventType.MilestoneDeleted:
          return props.roomSettings.featureSettings.disableActionPlan;
        case DealroomEventType.ContextualCommentReplyCreated:
        case DealroomEventType.ContextualCommentUpdated:
          return props.roomSettings.featureSettings.disableComments;
        case DealroomEventType.ConversationMessageCreated:
          return props.roomSettings.featureSettings.disableChat;
        case DealroomEventType.FileDeleted:
        case DealroomEventType.FileDownloaded:
        case DealroomEventType.FileUploaded:
          return props.roomSettings.featureSettings.disableFiles;
        default:
          return false;
      }
    };

    const disabledCtaTooltip = (notification: NotificationEvent) => {
      if (featureIsDisabled(notification.type)) {
        return t('hover_disabled_cta');
      }
      if (!isContentAvailable(notification)) {
        switch (notification.type) {
          case DealroomEventType.FileUploaded:
            return t('dr_activity_file_deleted');
          case DealroomEventType.TaskUpdated:
            return t('dr_activity_task_deleted');
          default:
            return '';
        }
      }
      return '';
    };

    const hasMetaDataContent = (notification: NotificationEvent) =>
      !!notification.metaData &&
      NotificationConstants.typesWithMetaDataContent.includes(notification.type);

    const getTime = (time: number) =>
      DateTime.fromMillis(time).toLocaleString(DateTime.TIME_SIMPLE);

    const getParticipantName = (participantId: string) => {
      const participant = props.participants.find(({ id }) => id === participantId);
      if (!participant) return '';

      return AvatarHelper.getDisplayName(participant);
    };

    const getParticipantDeletedAt = (participantId: string) =>
      props.participants.find(({ id }) => id === participantId)?.deletedAt;

    const isParticipantRemoved = (participantId: string) => {
      const participant = props.participants.find(({ id }) => id === participantId);
      return !participant || participant.deletedAt;
    };

    const canNavigateTo = (notification: NotificationEvent) => {
      if (!NotificationConstants.navigatableTypes.includes(notification.type)) {
        return false;
      }
      return !(
        NotificationConstants.typesWithMetaDataContent.includes(notification.type) &&
        !isContentAvailable(notification)
      );
    };

    return {
      ParagraphSize,
      ButtonType,
      showMore,
      visibleNotifications,
      nonVisibleNotifications,
      getTime,
      getParticipantName,
      featureIsDisabled,
      hasMetaDataContent,
      isParticipantRemoved,
      t,
      getParticipantDeletedAt,
      canNavigateTo,
      isContentAvailable,
      disabledCtaTooltip,
    };
  },
});
</script>

<style lang="scss" scoped>
.activity-card-container {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-50);

  &:not(:has(> button)) {
    margin-bottom: var(--spacing-200);
  }

  .card {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-100);
    padding: var(--spacing-100);
    border: 1px solid var(--gray-89);
    border-radius: var(--radius-medium);

    .unavailable-content {
      display: flex;
      gap: var(--spacing-50);

      p {
        font-style: italic;
      }
    }

    .go-there-button {
      width: 100%;
    }

    .card-header {
      display: flex;
      gap: var(--spacing-50);

      .removed {
        color: var(--gray-70);
      }

      .participant {
        font-weight: 600;
      }

      .time {
        color: var(--text-gray);
      }

      .mark-read-button {
        margin-left: auto;

        .blue-dot {
          background-color: var(--blue-45);
          height: 8px;
          width: 8px;
          border-radius: 50%;
        }
      }
    }

    .content {
      color: var(--text-gray);

      :deep(.mention) {
        color: var(--link-blue);
      }
    }
  }

  .show-more-button {
    height: 40px;
    display: flex;
    justify-content: center;
    gap: var(--spacing-50);
    cursor: pointer;
    color: var(--link-blue);
    background: none;
    border: none;
    margin-bottom: var(--spacing-50);

    h4 {
      color: var(--link-blue);
    }

    &:hover {
      h4 {
        text-decoration: underline;
      }
    }
  }
}
</style>
